import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Award, Trash2, Calendar, Users, Building, X, UserPlus, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Concorsi = () => {
  const { canManageConcorsi, isSindacatoAdmin, user } = useAuth();
  const canManage = canManageConcorsi();
  const isAdmin = isSindacatoAdmin();
  const isCittadino = user?.ruolo === 'CITTADINO';
  
  const [concorsi, setConcorsi] = useState([]);
  const [iscrizioni, setIscrizioni] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const concorsiRes = await axios.get(`${API_URL}/concorsi`);
      setConcorsi(concorsiRes.data);
      
      // Carica lo stato delle iscrizioni per ogni concorso
      const iscrizioniMap = {};
      for (const c of concorsiRes.data) {
        try {
          const iscRes = await axios.get(`${API_URL}/concorsi/${c.id}/mia-iscrizione`);
          iscrizioniMap[c.id] = iscRes.data;
        } catch {
          iscrizioniMap[c.id] = { iscritto: false };
        }
      }
      setIscrizioni(iscrizioniMap);
    } catch (error) {
      toast.error('Errore nel caricamento dei concorsi');
    } finally {
      setLoading(false);
    }
  };

  const handleIscrizione = async (concorsoId) => {
    try {
      await axios.post(`${API_URL}/concorsi/${concorsoId}/iscrivi`, { concorso_id: concorsoId });
      toast.success('Iscrizione effettuata con successo!');
      setIscrizioni(prev => ({
        ...prev,
        [concorsoId]: { iscritto: true }
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'iscrizione');
    }
  };

  const handleAnnullaIscrizione = async (concorsoId) => {
    if (!window.confirm('Vuoi annullare la tua iscrizione?')) return;
    try {
      await axios.delete(`${API_URL}/concorsi/${concorsoId}/iscrivi`);
      toast.success('Iscrizione annullata');
      setIscrizioni(prev => ({
        ...prev,
        [concorsoId]: { iscritto: false }
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    }
  };

  const handleStatusChange = async (id, stato) => {
    try {
      await axios.patch(`${API_URL}/concorsi/${id}/stato?stato=${stato}`);
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo concorso?')) return;
    try {
      await axios.delete(`${API_URL}/concorsi/${id}`);
      toast.success('Concorso eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getStatusBadge = (stato) => {
    const variants = {
      'APERTO': 'bg-green-100 text-green-800',
      'CHIUSO': 'bg-red-100 text-red-800',
      'IN_VALUTAZIONE': 'bg-amber-100 text-amber-800'
    };
    const labels = {
      'APERTO': 'Aperto',
      'CHIUSO': 'Chiuso',
      'IN_VALUTAZIONE': 'In Valutazione'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${variants[stato]}`}>
        {labels[stato]}
      </span>
    );
  };

  return (
    <div data-testid="concorsi-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <Award className="w-8 h-8" />
            Concorsi Pubblici
          </h1>
          <p className="text-slate-600 mt-1">
            {isCittadino ? 'Visualizza e candidati ai concorsi aperti' : 'Visualizza i concorsi delle aziende - Per creare concorsi usa Gestione Aziende'}
          </p>
        </div>
      </div>

      {/* Concorsi List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : concorsi.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Award className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessun concorso attivo</p>
            {canManage && <p className="text-sm text-slate-400">Clicca "Nuovo Concorso" per crearne uno</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {concorsi.map((concorso) => (
            <Card key={concorso.id} data-testid={`concorso-card-${concorso.id}`} className="border border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <CardTitle className="font-serif text-xl">{concorso.titolo}</CardTitle>
                      {getStatusBadge(concorso.stato)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                      {concorso.azienda_nome && (
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          {concorso.azienda_nome}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {concorso.posti_disponibili} posti
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Scadenza: {new Date(concorso.scadenza).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <Select
                        value={concorso.stato}
                        onValueChange={(value) => handleStatusChange(concorso.id, value)}
                      >
                        <SelectTrigger className="w-40" data-testid={`status-select-${concorso.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="APERTO">Aperto</SelectItem>
                          <SelectItem value="IN_VALUTAZIONE">In Valutazione</SelectItem>
                          <SelectItem value="CHIUSO">Chiuso</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`delete-concorso-${concorso.id}`}
                        onClick={() => handleDelete(concorso.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-1">Descrizione</h4>
                    <p className="text-slate-600 whitespace-pre-wrap">{concorso.descrizione}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-1">Requisiti</h4>
                    <p className="text-slate-600 whitespace-pre-wrap">{concorso.requisiti}</p>
                  </div>
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span>Creato da @{concorso.created_by_username || concorso.created_by} • {new Date(concorso.created_at).toLocaleDateString('it-IT')}</span>
                      <Badge variant="outline" className={concorso.modalita === 'PRESENZA' ? 'border-green-500 text-green-700' : 'border-blue-500 text-blue-700'}>
                        {concorso.modalita === 'PRESENZA' ? 'In Presenza' : 'Via Gestionale'}
                      </Badge>
                    </div>
                    {concorso.stato === 'APERTO' && (
                      <div>
                        {concorso.modalita === 'PRESENZA' ? (
                          // Iscrizione diretta solo per concorsi in PRESENZA
                          iscrizioni[concorso.id]?.iscritto ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAnnullaIscrizione(concorso.id)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`annulla-iscrizione-${concorso.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Annulla Iscrizione
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleIscrizione(concorso.id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`iscriviti-${concorso.id}`}
                            >
                              <UserPlus className="w-4 h-4 mr-1" />
                              Iscriviti
                            </Button>
                          )
                        ) : (
                          // Per concorsi via GESTIONALE - link alla pagina Curriculum
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.location.href = '/curriculum'}
                            className="border-blue-500 text-blue-700 hover:bg-blue-50"
                            data-testid={`invia-curriculum-${concorso.id}`}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Invia Curriculum
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Concorsi;
